import {
  disableFormSubmit,
  enableFormSubmit,
} from './utils/form-validation-utils';
import { getParameterByName } from './utils/url-utils';
import {
  retreiveFromSessionStorage,
  saveToSessionStorage,
} from './utils/browser-utils';

export default class GrecaptchaApi {
  constructor() {
    const { grecaptcha } = window;
    this.grecaptcha = grecaptcha;
    this.apiScript = document.getElementById('grecaptcha-api');
    this.assessmentUrl = '/GetRecaptchaAssessmentScore';
    this.siteKeysUrl = '/InitializeRecaptcha';
    this.actionLogsUrl = '/GetRecaptchaActionLogs';
    this.challengeKey = '';
    this.challengeUrl = '';
    this.scoreKey = '';
    this.scoreThreshold = this.getScoreThreshold();
    this.currentScore = -1;
    this.addOnClickActionLogs();
    this.initializeRecaptchaForms();
  }

  initializeRecaptchaForms() {
    const that = this;
    // If page has form(s) containing a recaptcha challenge,
    // disable the submit buttons until the getScore() callback has fired
    const recaptchaElements = document.querySelectorAll('[data-challenge-id]');
    [...recaptchaElements].forEach((element, index) => {
      // Prepare recaptcha targets
      if (!element.id) {
        element.id = `grecaptcha-container-${index}`;
      }
      const form = element.closest('form');
      if (form === null || form === undefined) return;
      const recaptchaChallengeIpt = form.querySelector('#RecaptchaChallenge');
      recaptchaChallengeIpt.classList.add('recaptcha-challenge-input');
      // If there is more than one challenge on a page, we *should* to update the id
      // of the challenge input to be unique
      if (recaptchaElements.length > 1) {
        // Update id on label
        const label = form.querySelector('[for="RecaptchaChallenge"]');
        label.setAttribute('for', `RecaptchaChallenge${index}`);
        // Update id on input
        recaptchaChallengeIpt.id = `RecaptchaChallenge${index}`;
      }
      disableFormSubmit(form);
    });
    // Get score and determine whether or not to show recaptcha challenge
    this.getScore({
      action: 'page_load',
      callback: () => {
        // Reenable the submit button and initialize the challenge, if required, on those forms
        [...recaptchaElements].forEach((element) => {
          const form = element.closest('form');
          if (form === null || form === undefined) return;
          // Get challenge size from form data attribute, if exists
          const { recaptchaChallengeSize } = element.dataset;
          const challengeSize = recaptchaChallengeSize;
          enableFormSubmit(form);
          that.enableFormRecaptcha({ challengeSize, form });
        });
      },
    });
  }

  displayChallenge(score) {
    const { scoreThreshold } = this;
    return score < scoreThreshold;
  }

  enableFormRecaptcha(options) {
    const {
      challengeSize,
      form,
    } = options;
    if (form === null) {
      return;
    }
    const challengeIdContainer = form.querySelector('[data-challenge-id]');
    if (challengeIdContainer === null) {
      return;
    }
    const challengeId = challengeIdContainer.id;
    const recaptchaChallengeIpt = form.querySelector('.recaptcha-challenge-input');
    if (recaptchaChallengeIpt === null) {
      return;
    }
    if (recaptchaChallengeIpt.value !== '') {
      return;
    }
    if (this.displayChallenge(this.currentScore)) {
      this.getChallenge({
        callback: () => {
          const wrapper = form.querySelector('.grecaptcha-challenge-wrapper');
          wrapper.classList.remove('hidden');
        },
        challengeId,
        challengeSize,
        verifyCallback: (response) => {
          recaptchaChallengeIpt.value = response;
        },
      });
    } else {
      recaptchaChallengeIpt.value = 'no-challenge-needed';
    }
  }

  getKeys() {
    // Check sessionStorage for site keys
    // If they exist, pass along
    const keys = retreiveFromSessionStorage('__grecaptchakeys');
    if (keys) {
      const {
        challengeKey,
        challengeUrl,
        scoreKey,
      } = keys;
      if (challengeKey && challengeUrl && scoreKey) {
        return new Promise((resolve) => {
          // console.log('grecaptcha ----> get keys from sessionStorage');
          resolve(keys);
        });
      }
    }
    // console.log('grecaptcha ----> only execute if no keys found in sessionStorage');
    const url = this.siteKeysUrl;
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'POST',
      })
        .then((response) => {
          if (response.ok) {
            response.json().then((data) => {
              saveToSessionStorage('__grecaptchakeys', data);
              resolve(data);
            });
          } else {
            response.json().then(reject);
          }
        })
        .catch(console.error);
    });
  }

  getToken(action, sitekey) {
    const { grecaptcha } = this;
    return new Promise((resolve, reject) => {
      grecaptcha.enterprise.ready(() => {
        grecaptcha.enterprise.execute(sitekey, { action })
          .then((data) => {
            if (data) {
              resolve(data);
            } else {
              reject();
            }
          });
      });
    });
  }

  getAssessmentScore(action, token) {
    const url = `${this.assessmentUrl}?expectedAction=${action}&token=${token}`;
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'POST',
      })
        .then((response) => {
          if (response.ok) {
            response.json().then(resolve);
          } else {
            response.text().then(reject);
          }
        })
        .catch(console.error);
    });
  }

  getChallenge(options) {
    const {
      challengeKey,
      challengeUrl,
      grecaptcha,
    } = this;
    const {
      callback,
      challengeId,
      challengeSize,
      verifyCallback,
    } = options;
    if (!grecaptcha) {
      return -1;
    }
    // Add script
    const grecaptchaChallengeScript = document.createElement('script');
    grecaptchaChallengeScript.src = challengeUrl;
    document.body.appendChild(grecaptchaChallengeScript);
    grecaptchaChallengeScript.addEventListener('load', () => {
      const recaptchaChallenge = grecaptcha.enterprise.render(challengeId, {
        callback: verifyCallback,
        sitekey: challengeKey,
        size: challengeSize || 'normal',
      });
      if (callback) {
        callback(recaptchaChallenge);
      }
    });
    return false;
  }

  logAction(options) {
    const { grecaptcha } = this;
    const {
      action,
      callback,
    } = options;
    if (!grecaptcha) {
      if (callback) {
        callback('');
      }
      return;
    }
    // Get keys
    this.getKeys()
      .then((keys) => {
        const {
          challengeKey,
          challengeUrl,
          scoreKey,
        } = keys;
        this.challengeKey = challengeKey;
        this.challengeUrl = challengeUrl;
        this.scoreKey = scoreKey;
        this.getToken(action, this.scoreKey)
          .then((token) => {
            if (callback) {
              callback(token);
            }
          })
          .catch(() => {
            if (callback) {
              callback('');
            }
          });
      });
  }

  getScoreThreshold() {
    const { apiScript } = this;
    let scoreThreshold = '';
    if (apiScript) {
      scoreThreshold = getParameterByName('scoreThreshold', apiScript.src);
    }
    return scoreThreshold !== '' ? scoreThreshold : '.5';
  }

  /*
    Returns an assessment score for the likelihood user is a human
    Error Codes:
    0: Error retrieving token, show highest form of RECAPTCHA
    -1: Error loading RECAPTCHA
    -2: Asssessment service not available
    -3: No score available
    -4: expectedAction returned from the RECAPTCHA endpoint does not match submitted action
  */
  getScore(options) {
    const { grecaptcha } = this;
    const {
      action,
      callback,
    } = options;
    if (!grecaptcha || !callback) {
      this.currentScore = -1;
      return this.currentScore;
    }
    // Get keys
    this.getKeys()
      .then((keys) => {
        const {
          challengeKey,
          challengeUrl,
          scoreKey,
        } = keys;
        this.challengeKey = challengeKey;
        this.challengeUrl = challengeUrl;
        this.scoreKey = scoreKey;
        this.getToken(action, this.scoreKey)
          .then((token) => {
            this.getAssessmentScore(action, token)
              .then((data) => {
                try {
                  const { event, riskAnalysis } = data;
                  const { score } = riskAnalysis;
                  const { expectedAction } = event;
                  if (expectedAction !== action) {
                    this.currentScore = -4;
                    callback(this.currentScore);
                  }
                  this.currentScore = score;
                  callback(score);
                } catch (error) {
                  this.currentScore = -3;
                  callback(this.currentScore);
                }
              })
              .catch(() => {
                this.currentScore = -2;
                callback(this.currentScore);
              });
          })
          .catch((error) => {
            console.log(error);
            this.currentScore = 0;
            callback(this.currentScore);
          });
      });
    this.currentScore = -1;
    return this.currentScore;
  }

  getRecaptchaActionLogs() {
    const url = this.actionLogsUrl;
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: 'POST',
      })
        .then((response) => {
          if (response.ok) {
            response.json().then(resolve);
          } else {
            response.json().then(reject);
          }
        })
        .catch(console.error);
    });
  }

  addOnClickActionLogs() {
    const that = this;
    this.getRecaptchaActionLogs()
      .then((actions) => {
        actions.forEach((item) => {
          const {
            action,
            url,
          } = item;
          document.addEventListener('click', (event) => {
            const { target } = event;
            const { href } = target;
            if (href && href === url) {
              that.logAction({ action });
            }
          });
        });
      });
  }
}

window.grecaptchaApi = new GrecaptchaApi();
// Can probably delete this callback. Unsure if it's being referenced by the endpoint
window.onRecaptchaChallengeLoad = () => {
  console.log('window ---> onRecaptchaChallengeLoad');
};
